import { Store } from '../state/store';
import { ApiService } from './api';
import { SSEParser } from './sse';
import type { Image, ActionStep, Reaction } from '../state/types';

function extractTargetIdFromReaction(content: string): string | null {
    if (!content.startsWith('[REACTION]')) return null;
    
    const msgMatch = content.match(/ to msg_([a-f0-9]+)/);
    if (msgMatch) return msgMatch[1];
    
    const assistantMatch = content.match(/ to assistant \(msg_([a-f0-9]+)\)/);
    if (assistantMatch) return assistantMatch[1];
    
    return null;
}

interface StreamBuffer {
    assistantId: string;
    content: string;
    actionSteps: ActionStep[];
    reasoningContent: string;
}

interface InitEvent {
    type: 'init';
    user_message_id: string | null;
    assistant_message_id: string;
    user_flags?: string[];
}

interface ReactionChunk {
    reaction: Reaction;
    target_message_id: string;
    flag?: string;
    is_terminal: boolean;
}

export class StreamingManager {
    private store: Store;
    private api: ApiService;
    private abortControllers: Map<string, AbortController> = new Map();
    private streamingMessages: Map<string, any[]> = new Map();
    private streamingMessageBuffers: Map<string, StreamBuffer> = new Map();
    private onUpdateMessage: ((assistantId: string, content: string) => void) | null = null;
    private onStreamingComplete: (() => void) | null = null;
    private onInit: ((assistantId: string) => void) | null = null;
    private onUpdateActionSteps: ((assistantId: string, actionSteps: ActionStep[]) => void) | null = null;

    constructor(store: Store, api: ApiService) {
        this.store = store;
        this.api = api;
    }

    setOnInit(callback: (assistantId: string) => void): void {
        this.onInit = callback;
    }

    setOnUpdateMessage(callback: (assistantId: string, content: string) => void): void {
        this.onUpdateMessage = callback;
    }

    setOnStreamingComplete(callback: () => void): void {
        this.onStreamingComplete = callback;
    }

    setOnUpdateActionSteps(callback: (assistantId: string, actionSteps: ActionStep[]) => void): void {
        this.onUpdateActionSteps = callback;
    }

    getAbortController(conversationId: string): AbortController | undefined {
        return this.abortControllers.get(conversationId);
    }

    hasStreamBuffer(conversationId: string): boolean {
        return this.streamingMessageBuffers.has(conversationId);
    }

    getStreamBuffer(conversationId: string): StreamBuffer | undefined {
        return this.streamingMessageBuffers.get(conversationId);
    }

    saveStreamingMessages(conversationId: string, messages: any[]): void {
        this.streamingMessages.set(conversationId, messages);
    }

    getStreamingMessages(conversationId: string): any[] | undefined {
        return this.streamingMessages.get(conversationId);
    }

    clearStreamBuffer(conversationId: string): void {
        this.streamingMessageBuffers.delete(conversationId);
    }

    async startStream(
        conversationId: string,
        content: string,
        model: string,
        images: Image[]
    ): Promise<void> {
        let assistantId = '';
        let assistantContent = '';

        this.store.moveConversationToTop(conversationId);

        try {
            const controller = new AbortController();
            this.abortControllers.set(conversationId, controller);
            const response = await this.api.chatStream(
                conversationId,
                content,
                model,
                images.length > 0 ? images : undefined,
                controller.signal
            );

            const reader = response.body?.getReader();
            if (!reader) throw new Error('No reader available');

            const decoder = new TextDecoder();
            const parser = new SSEParser();

            while (true) {
                const { done, value } = await reader.read();
                if (done) break;

                const chunk = decoder.decode(value);
                const messages = parser.process(chunk);

                for (const data of messages) {
                    try {
                        const parsed = JSON.parse(data);

                        if (parsed.id === 'init' && parsed.content) {
                            const initEvent: InitEvent = JSON.parse(parsed.content);
                            assistantId = initEvent.assistant_message_id;
                            
                            if (initEvent.user_message_id) {
                                this.store.addMessage({
                                    id: initEvent.user_message_id,
                                    role: 'user',
                                    content,
                                    created_at: new Date().toISOString(),
                                    images: images.length > 0 ? images : undefined,
                                    flags: initEvent.user_flags,
                                });
                            }
                            
                            this.store.addMessage({
                                id: assistantId,
                                role: 'assistant',
                                content: '',
                                created_at: new Date().toISOString(),
                            });
                            
                            if (this.onInit && !initEvent.user_flags?.includes('hidden')) {
                                this.onInit(assistantId);
                            }
                            continue;
                        }

                        if (parsed.title) {
                            const isUUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(parsed.title);
                            if (isUUID && !this.store.isJobActive(conversationId)) {
                                this.store.setActiveJob(conversationId, parsed.title, this.api);
                            } else if (!isUUID) {
                                this.store.updateConversationTitle(conversationId, parsed.title);
                            }
                        }

                        if (parsed.content && assistantId) {
                            assistantContent += parsed.content;
                            this.store.updateStreamingState(conversationId, { hasReceivedFirstToken: true }, true);

                            this.streamingMessageBuffers.set(conversationId, {
                                assistantId: assistantId,
                                content: assistantContent,
                                actionSteps: this.streamingMessageBuffers.get(conversationId)?.actionSteps || [],
                                reasoningContent: this.streamingMessageBuffers.get(conversationId)?.reasoningContent || '',
                            });

                            const currentConversationId = this.store.getCurrentConversationId();
                            if (currentConversationId === conversationId) {
                                this.store.updateMessage(assistantId, { content: assistantContent }, true);
                                if (this.onUpdateMessage) {
                                    this.onUpdateMessage(assistantId, assistantContent);
                                }
                            }
                        }

                        if (parsed.reasoning_content && assistantId) {
                            const buffer = this.streamingMessageBuffers.get(conversationId);
                            const currentReasoning = buffer?.reasoningContent || '';
                            const newReasoning = currentReasoning + parsed.reasoning_content;
                            let currentSteps = [...(buffer?.actionSteps || [])];
                            
                            const lastStep = currentSteps[currentSteps.length - 1];
                            if (lastStep && lastStep.type === 'reasoning') {
                                currentSteps = [
                                    ...currentSteps.slice(0, -1),
                                    { ...lastStep, content: (lastStep.content || '') + parsed.reasoning_content }
                                ];
                            } else {
                                currentSteps = [...currentSteps, { type: 'reasoning', content: parsed.reasoning_content }];
                            }
                            
                            this.streamingMessageBuffers.set(conversationId, {
                                assistantId: assistantId,
                                content: buffer?.content || assistantContent,
                                actionSteps: currentSteps,
                                reasoningContent: newReasoning,
                            });
                            
                            this.store.updateMessageActionSteps(assistantId, currentSteps);
                            if (this.onUpdateActionSteps) {
                                this.onUpdateActionSteps(assistantId, currentSteps);
                            }
                        }

                        if (parsed.action_step && assistantId) {
                            const buffer = this.streamingMessageBuffers.get(conversationId);
                            const currentSteps = [...(buffer?.actionSteps || []), parsed.action_step as ActionStep];
                            
                            this.streamingMessageBuffers.set(conversationId, {
                                assistantId: assistantId,
                                content: buffer?.content || assistantContent,
                                actionSteps: currentSteps,
                                reasoningContent: '',
                            });
                            
                            this.store.updateMessageActionSteps(assistantId, currentSteps);
                            if (this.onUpdateActionSteps) {
                                this.onUpdateActionSteps(assistantId, currentSteps);
                            }
                        }

if (parsed.reaction && parsed.target_message_id) {
                            const messages = this.store.getMessages();
                            let targetMessage = messages.find(m => {
                                const cleanTargetId = parsed.target_message_id.replace(/^msg_/, '');
                                const cleanMsgId = m.id.replace(/-/g, '');
                                return cleanMsgId.startsWith(cleanTargetId) || 
                                       cleanTargetId === cleanMsgId.substring(0, 8);
                            });
                            
                            if (targetMessage) {
                                if (targetMessage.flags?.includes('hidden') && 
                                    targetMessage.content?.startsWith('[REACTION]')) {
                                    const originalTargetId = extractTargetIdFromReaction(targetMessage.content);
                                    if (originalTargetId) {
                                        const originalMessage = messages.find(m => {
                                            const cleanMsgId = m.id.replace(/-/g, '');
                                            return cleanMsgId.startsWith(originalTargetId) || 
                                                   originalTargetId === cleanMsgId.substring(0, 8);
                                        });
                                        if (originalMessage) {
                                            targetMessage = originalMessage;
                                        }
                                    }
                                }
                                
                                const existingReactions = targetMessage.reactions || [];
                                const filteredReactions = existingReactions.filter(r => r.from_role !== 'assistant');
                                const updatedReactions = [...filteredReactions, parsed.reaction];
                                this.store.updateMessageReactions(targetMessage.id, updatedReactions);
                            }
                        }

                        if (parsed.is_terminal && parsed.flag === 'disregard_ack' && parsed.target_message_id) {
                            const messages = this.store.getMessages();
                            let targetMessage = messages.find(m => {
                                const cleanTargetId = parsed.target_message_id.replace(/^msg_/, '');
                                const cleanMsgId = m.id.replace(/-/g, '');
                                return cleanMsgId.startsWith(cleanTargetId) || 
                                       cleanTargetId === cleanMsgId.substring(0, 8);
                            });
                            
                            if (targetMessage) {
                                if (targetMessage.flags?.includes('hidden') && 
                                    targetMessage.content?.startsWith('[REACTION]')) {
                                    const originalTargetId = extractTargetIdFromReaction(targetMessage.content);
                                    if (originalTargetId) {
                                        const originalMessage = messages.find(m => {
                                            const cleanMsgId = m.id.replace(/-/g, '');
                                            return cleanMsgId.startsWith(originalTargetId) || 
                                                   originalTargetId === cleanMsgId.substring(0, 8);
                                        });
                                        if (originalMessage) {
                                            targetMessage = originalMessage;
                                        }
                                    }
                                }
                                
                                const existingFlags = targetMessage.flags || [];
                                if (!existingFlags.includes('disregard_ack')) {
                                    this.store.updateMessageFlags(targetMessage.id, [...existingFlags, 'disregard_ack']);
                                }
                            }
                        }

                        if (parsed.is_terminal && parsed.flags && assistantId) {
                            this.store.updateMessageFlags(assistantId, parsed.flags);
                        }

                        if (parsed.id === 'error' && assistantId) {
                            assistantContent = parsed.content;
                            this.store.updateMessage(assistantId, { content: parsed.content }, true);
                            if (this.onUpdateMessage) {
                                this.onUpdateMessage(assistantId, parsed.content);
                            }
                            break;
                        }
                    } catch (e) {
                        console.error('Failed to parse SSE data:', data, e);
                    }
                }
            }

        } catch (error) {
            if (error instanceof Error && error.name !== 'AbortError') {
                console.error('Error streaming response:', error);
                this.store.addMessage({
                    id: crypto.randomUUID(),
                    role: 'assistant',
                    content: `Error: ${error.message}`,
                    created_at: new Date().toISOString(),
                });
            }
        } finally {
            const buffer = this.streamingMessageBuffers.get(conversationId);
            const actionSteps = buffer?.actionSteps || [];
            const onlyReactTool = actionSteps.length >= 1 && actionSteps.every(s => s.name === 'react');
            
            this.streamingMessageBuffers.delete(conversationId);
            this.streamingMessages.delete(conversationId);
            this.store.stopStreaming(conversationId);
            this.abortControllers.delete(conversationId);

            if (assistantId && assistantContent === '') {
                const messages = this.store.getMessages();
                const msg = messages.find(m => m.id === assistantId);
                const alreadyHidden = msg?.flags?.includes('hidden');
                
                if (!alreadyHidden) {
                    if (onlyReactTool) {
                        console.log('[REACTION] Only react tool used, hiding assistant message');
                        this.store.updateMessageFlags(assistantId, ['hidden']);
                    } else {
                        this.store.removeMessage(assistantId, true);
                    }
                }
            }

            if (this.onStreamingComplete) {
                this.onStreamingComplete();
            }
        }
    }

    async tryAgain(
        conversationId: string,
        messageId: string,
        model?: string
    ): Promise<{ assistantId: string; content: string }> {
        let assistantId = '';
        let assistantContent = '';

        this.store.moveConversationToTop(conversationId);

        try {
            const controller = new AbortController();
            this.abortControllers.set(conversationId, controller);
            const response = await this.api.tryAgain(messageId, conversationId, model, controller.signal);

            const reader = response.body?.getReader();
            if (!reader) throw new Error('No reader available');

            const decoder = new TextDecoder();
            const parser = new SSEParser();

            while (true) {
                const { done, value } = await reader.read();
                if (done) break;

                const chunk = decoder.decode(value);
                const messages = parser.process(chunk);

                for (const data of messages) {
                    try {
                        const parsed = JSON.parse(data);

                        if (parsed.id === 'init' && parsed.content) {
                            const initEvent: InitEvent = JSON.parse(parsed.content);
                            assistantId = initEvent.assistant_message_id;
                            
                            this.store.addMessage({
                                id: assistantId,
                                role: 'assistant',
                                content: '',
                                created_at: new Date().toISOString(),
                            });
                            
                            if (this.onInit) {
                                this.onInit(assistantId);
                            }
                            continue;
                        }

                        if (parsed.title) {
                            const isUUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(parsed.title);
                            if (isUUID && !this.store.isJobActive(conversationId)) {
                                this.store.setActiveJob(conversationId, parsed.title, this.api);
                            }
                        }

                        if (parsed.content && assistantId) {
                            assistantContent += parsed.content;
                            const streamingState = this.store.getStreamingState(conversationId);
                            if (!streamingState.hasReceivedFirstToken) {
                                this.store.updateStreamingState(conversationId, { hasReceivedFirstToken: true }, true);
                            }

                            this.streamingMessageBuffers.set(conversationId, {
                                assistantId: assistantId,
                                content: assistantContent,
                                actionSteps: this.streamingMessageBuffers.get(conversationId)?.actionSteps || [],
                                reasoningContent: this.streamingMessageBuffers.get(conversationId)?.reasoningContent || '',
                            });

                            const currentConversationId = this.store.getCurrentConversationId();
                            if (currentConversationId === conversationId) {
                                this.store.updateMessage(assistantId, { content: assistantContent }, true);
                                if (this.onUpdateMessage) {
                                    this.onUpdateMessage(assistantId, assistantContent);
                                }
                            }
                        }

                        if (parsed.reasoning_content && assistantId) {
                            const buffer = this.streamingMessageBuffers.get(conversationId);
                            const currentReasoning = buffer?.reasoningContent || '';
                            const newReasoning = currentReasoning + parsed.reasoning_content;
                            let currentSteps = [...(buffer?.actionSteps || [])];
                            
                            const lastStep = currentSteps[currentSteps.length - 1];
                            if (lastStep && lastStep.type === 'reasoning') {
                                currentSteps = [
                                    ...currentSteps.slice(0, -1),
                                    { ...lastStep, content: (lastStep.content || '') + parsed.reasoning_content }
                                ];
                            } else {
                                currentSteps = [...currentSteps, { type: 'reasoning', content: parsed.reasoning_content }];
                            }
                            
                            this.streamingMessageBuffers.set(conversationId, {
                                assistantId: assistantId,
                                content: buffer?.content || assistantContent,
                                actionSteps: currentSteps,
                                reasoningContent: newReasoning,
                            });
                            
                            this.store.updateMessageActionSteps(assistantId, currentSteps);
                            if (this.onUpdateActionSteps) {
                                this.onUpdateActionSteps(assistantId, currentSteps);
                            }
                        }

                        if (parsed.action_step && assistantId) {
                            const buffer = this.streamingMessageBuffers.get(conversationId);
                            const currentSteps = [...(buffer?.actionSteps || []), parsed.action_step as ActionStep];
                            
                            this.streamingMessageBuffers.set(conversationId, {
                                assistantId: assistantId,
                                content: buffer?.content || assistantContent,
                                actionSteps: currentSteps,
                                reasoningContent: '',
                            });
                            
                            this.store.updateMessageActionSteps(assistantId, currentSteps);
                            if (this.onUpdateActionSteps) {
                                this.onUpdateActionSteps(assistantId, currentSteps);
                            }
                        }

if (parsed.reaction && parsed.target_message_id) {
                            const messages = this.store.getMessages();
                            let targetMessage = messages.find(m => {
                                const cleanTargetId = parsed.target_message_id.replace(/^msg_/, '');
                                const cleanMsgId = m.id.replace(/-/g, '');
                                return cleanMsgId.startsWith(cleanTargetId) || 
                                       cleanTargetId === cleanMsgId.substring(0, 8);
                            });
                            
                            if (targetMessage) {
                                if (targetMessage.flags?.includes('hidden') && 
                                    targetMessage.content?.startsWith('[REACTION]')) {
                                    const originalTargetId = extractTargetIdFromReaction(targetMessage.content);
                                    if (originalTargetId) {
                                        const originalMessage = messages.find(m => {
                                            const cleanMsgId = m.id.replace(/-/g, '');
                                            return cleanMsgId.startsWith(originalTargetId) || 
                                                   originalTargetId === cleanMsgId.substring(0, 8);
                                        });
                                        if (originalMessage) {
                                            targetMessage = originalMessage;
                                        }
                                    }
                                }
                                
                                const existingReactions = targetMessage.reactions || [];
                                const filteredReactions = existingReactions.filter(r => r.from_role !== 'assistant');
                                const updatedReactions = [...filteredReactions, parsed.reaction];
                                this.store.updateMessageReactions(targetMessage.id, updatedReactions);
                            }
                        }

                        if (parsed.is_terminal && parsed.flag === 'disregard_ack' && parsed.target_message_id) {
                            const messages = this.store.getMessages();
                            let targetMessage = messages.find(m => {
                                const cleanTargetId = parsed.target_message_id.replace(/^msg_/, '');
                                const cleanMsgId = m.id.replace(/-/g, '');
                                return cleanMsgId.startsWith(cleanTargetId) || 
                                       cleanTargetId === cleanMsgId.substring(0, 8);
                            });
                            
                            if (targetMessage) {
                                if (targetMessage.flags?.includes('hidden') && 
                                    targetMessage.content?.startsWith('[REACTION]')) {
                                    const originalTargetId = extractTargetIdFromReaction(targetMessage.content);
                                    if (originalTargetId) {
                                        const originalMessage = messages.find(m => {
                                            const cleanMsgId = m.id.replace(/-/g, '');
                                            return cleanMsgId.startsWith(originalTargetId) || 
                                                   originalTargetId === cleanMsgId.substring(0, 8);
                                        });
                                        if (originalMessage) {
                                            targetMessage = originalMessage;
                                        }
                                    }
                                }
                                
                                const existingFlags = targetMessage.flags || [];
                                if (!existingFlags.includes('disregard_ack')) {
                                    this.store.updateMessageFlags(targetMessage.id, [...existingFlags, 'disregard_ack']);
                                }
                            }
                        }

                        if (parsed.id === 'error' && assistantId) {
                            assistantContent = parsed.content;
                            this.store.updateMessage(assistantId, { content: parsed.content }, true);
                            if (this.onUpdateMessage) {
                                this.onUpdateMessage(assistantId, parsed.content);
                            }
                            break;
                        }
                    } catch (e) {
                        console.error('Failed to parse SSE data:', data, e);
                    }
                }
            }

        } catch (error) {
            if (error instanceof Error && error.name !== 'AbortError') {
                console.error('Error streaming response:', error);
                this.store.addMessage({
                    id: crypto.randomUUID(),
                    role: 'assistant',
                    content: `Error: ${error.message}`,
                    created_at: new Date().toISOString(),
                });
            }
        } finally {
            const buffer = this.streamingMessageBuffers.get(conversationId);
            const actionSteps = buffer?.actionSteps || [];
            const onlyReactTool = actionSteps.length >= 1 && actionSteps.every(s => s.name === 'react');
            
            this.streamingMessageBuffers.delete(conversationId);
            this.streamingMessages.delete(conversationId);
            this.store.stopStreaming(conversationId);
            this.abortControllers.delete(conversationId);

            if (assistantId && assistantContent === '') {
                const messages = this.store.getMessages();
                const msg = messages.find(m => m.id === assistantId);
                const alreadyHidden = msg?.flags?.includes('hidden');
                
                if (!alreadyHidden) {
                    if (onlyReactTool) {
                        this.store.updateMessageFlags(assistantId, ['hidden']);
                    } else {
                        this.store.removeMessage(assistantId, true);
                    }
                }
            }

            if (this.onStreamingComplete) {
                this.onStreamingComplete();
            }
        }
        
        return { assistantId, content: assistantContent };
    }

    cleanupConversation(conversationId: string): void {
        this.streamingMessages.delete(conversationId);
        this.streamingMessageBuffers.delete(conversationId);
        const controller = this.abortControllers.get(conversationId);
        if (controller) {
            controller.abort();
            this.abortControllers.delete(conversationId);
        }
    }

    async stopStream(conversationId: string): Promise<void> {
        const controller = this.abortControllers.get(conversationId);
        if (controller) {
            controller.abort();
        }
        
        try {
            await this.api.stopStream(conversationId);
        } catch (error) {
            console.error('Error stopping stream on backend:', error);
        }
        
        this.abortControllers.delete(conversationId);
        this.streamingMessageBuffers.delete(conversationId);
        this.streamingMessages.delete(conversationId);
        this.store.stopStreaming(conversationId);
    }

    async resumeStream(conversationId: string): Promise<boolean> {
        let assistantId = '';
        let assistantContent = '';

        try {
            const controller = new AbortController();
            this.abortControllers.set(conversationId, controller);
            const response = await this.api.resumeStream(conversationId);

            const contentType = response.headers.get('content-type') || '';
            if (contentType.includes('application/json')) {
                const data = await response.json();
                if (data.status === 'none' || data.status === 'cancelled' || data.status === 'complete') {
                    this.abortControllers.delete(conversationId);
                    return false;
                }
            }

            const reader = response.body?.getReader();
            if (!reader) {
                this.abortControllers.delete(conversationId);
                return false;
            }

            this.store.startStreaming(conversationId, false);
            this.store.moveConversationToTop(conversationId);
            const decoder = new TextDecoder();
            const parser = new SSEParser();

            while (true) {
                const { done, value } = await reader.read();
                if (done) break;

                const chunk = decoder.decode(value);
                const messages = parser.process(chunk);

                for (const data of messages) {
                    try {
                        const parsed = JSON.parse(data);

                        if (parsed.id === 'init' && parsed.content) {
                            const initEvent: InitEvent = JSON.parse(parsed.content);
                            assistantId = initEvent.assistant_message_id;
                            
                            const existingMessages = this.store.getMessages();
                            const existingAssistant = existingMessages.find(m => m.id === assistantId);
                            
                            if (!existingAssistant) {
                                this.store.addMessage({
                                    id: assistantId,
                                    role: 'assistant',
                                    content: '',
                                    created_at: new Date().toISOString(),
                                });
                            }
                            
                            if (this.onInit) {
                                this.onInit(assistantId);
                            }
                            continue;
                        }

                        if (parsed.title && assistantId) {
                            const isUUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(parsed.title);
                            if (!isUUID) {
                                this.store.updateConversationTitle(conversationId, parsed.title);
                            }
                        }

                        if (parsed.content && assistantId) {
                            assistantContent += parsed.content;
                            this.store.updateStreamingState(conversationId, { hasReceivedFirstToken: true }, true);

                            this.streamingMessageBuffers.set(conversationId, {
                                assistantId: assistantId,
                                content: assistantContent,
                                actionSteps: this.streamingMessageBuffers.get(conversationId)?.actionSteps || [],
                                reasoningContent: this.streamingMessageBuffers.get(conversationId)?.reasoningContent || '',
                            });

                            const currentConversationId = this.store.getCurrentConversationId();
                            if (currentConversationId === conversationId) {
                                this.store.updateMessage(assistantId, { content: assistantContent }, true);
                                if (this.onUpdateMessage) {
                                    this.onUpdateMessage(assistantId, assistantContent);
                                }
                            }
                        }

                        if (parsed.reasoning_content && assistantId) {
                            const buffer = this.streamingMessageBuffers.get(conversationId);
                            const currentReasoning = buffer?.reasoningContent || '';
                            const newReasoning = currentReasoning + parsed.reasoning_content;
                            let currentSteps = [...(buffer?.actionSteps || [])];
                            
                            const lastStep = currentSteps[currentSteps.length - 1];
                            if (lastStep && lastStep.type === 'reasoning') {
                                currentSteps = [
                                    ...currentSteps.slice(0, -1),
                                    { ...lastStep, content: (lastStep.content || '') + parsed.reasoning_content }
                                ];
                            } else {
                                currentSteps = [...currentSteps, { type: 'reasoning', content: parsed.reasoning_content }];
                            }
                            
                            this.streamingMessageBuffers.set(conversationId, {
                                assistantId: assistantId,
                                content: buffer?.content || assistantContent,
                                actionSteps: currentSteps,
                                reasoningContent: newReasoning,
                            });
                            
                            this.store.updateMessageActionSteps(assistantId, currentSteps);
                            if (this.onUpdateActionSteps) {
                                this.onUpdateActionSteps(assistantId, currentSteps);
                            }
                        }

                        if (parsed.action_step && assistantId) {
                            const buffer = this.streamingMessageBuffers.get(conversationId);
                            const currentSteps = [...(buffer?.actionSteps || []), parsed.action_step as ActionStep];
                            
                            this.streamingMessageBuffers.set(conversationId, {
                                assistantId: assistantId,
                                content: buffer?.content || assistantContent,
                                actionSteps: currentSteps,
                                reasoningContent: '',
                            });
                            
                            this.store.updateMessageActionSteps(assistantId, currentSteps);
                            if (this.onUpdateActionSteps) {
                                this.onUpdateActionSteps(assistantId, currentSteps);
                            }
                        }

                        if (parsed.reaction && parsed.target_message_id) {
                            const messages = this.store.getMessages();
                            let targetMessage = messages.find(m => {
                                const cleanTargetId = parsed.target_message_id.replace(/^msg_/, '');
                                const cleanMsgId = m.id.replace(/-/g, '');
                                return cleanMsgId.startsWith(cleanTargetId) || 
                                       cleanTargetId === cleanMsgId.substring(0, 8);
                            });
                            
                            if (targetMessage) {
                                const existingReactions = targetMessage.reactions || [];
                                const filteredReactions = existingReactions.filter(r => r.from_role !== 'assistant');
                                const updatedReactions = [...filteredReactions, parsed.reaction];
                                this.store.updateMessageReactions(targetMessage.id, updatedReactions);
                            }
                        }

                        if (parsed.id === 'error' && assistantId) {
                            assistantContent = parsed.content;
                            this.store.updateMessage(assistantId, { content: parsed.content }, true);
                            if (this.onUpdateMessage) {
                                this.onUpdateMessage(assistantId, parsed.content);
                            }
                            break;
                        }
                    } catch (e) {
                        console.error('Failed to parse SSE data:', data, e);
                    }
                }
            }

            return true;
        } catch (error) {
            if (error instanceof Error && error.name !== 'AbortError') {
                console.error('Error resuming stream:', error);
            }
            return false;
        } finally {
            this.streamingMessageBuffers.delete(conversationId);
            this.store.stopStreaming(conversationId);
            this.abortControllers.delete(conversationId);

            if (assistantId && assistantContent === '') {
                const messages = this.store.getMessages();
                const msg = messages.find(m => m.id === assistantId);
                const alreadyHidden = msg?.flags?.includes('hidden');
                if (!alreadyHidden) {
                    this.store.removeMessage(assistantId, true);
                }
            }

            if (this.onStreamingComplete) {
                this.onStreamingComplete();
            }
        }
    }
}